import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, UsersIcon } from 'lucide-react';
import { customersOf, MerchantCustomer, initials } from '../data/customers';
import { seedOrders, Order } from '../data/orders';
import { CustomerProfile } from './customers/CustomerProfile';
import { OrderDetailDrawer } from './orders/OrderDetailDrawer';
import { PremiumSelect } from './PremiumSelect';

/**
 * Customers — everyone who has connected with THIS merchant, whether or not
 * they ever ordered. Signing in on the merchant's shop is enough. Deliberately
 * not a CRM: who they are, how they arrived, and what they've done here.
 */
export function BusinessCustomers() {
  // In the real product this comes from the signed-in merchant. Every read is
  // scoped to it, which is what keeps one merchant out of another's data.
  const merchantId = 'easylease';
  const all = useMemo(() => customersOf(merchantId), [merchantId]);
  const [q, setQ] = useState('');
  const [source, setSource] = useState('All sources');
  const [activity, setActivity] = useState('Any activity');
  const [open, setOpen] = useState<MerchantCustomer | null>(null);
  const [openOrder, setOpenOrder] = useState<Order | null>(null);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter(c => {
      if (needle && !`${c.name} ${c.phone} ${c.email ?? ''}`.toLowerCase().includes(needle)) return false;
      if (source !== 'All sources' && c.source !== source) return false;
      if (activity === 'Has orders' && c.orders === 0) return false;
      if (activity === 'No orders yet' && c.orders > 0) return false;
      if (activity === 'Active this week' && c.lastActivityDays > 7) return false;
      return true;
    });
  }, [all, q, source, activity]);
  const newThisMonth = all.filter(c => c.lastActivityDays <= 30).length;
  const withOrders = all.filter(c => c.orders > 0).length;

  // Orders are never duplicated — the profile reads the same Orders records,
  // matched to this customer by phone.
  const ordersFor = (c: MerchantCustomer) => seedOrders.filter(o => o.customer.phone === c.phone);
  return <div className="px-4 lg:px-8 py-6" data-id="element-483" data-mp="5vekajauy6cb">
      <div className="mx-auto max-w-6xl" data-id="element-484" data-mp="5s8ybcxizkkt">
        {/* The nav tab already says "Customers" — this only adds meaning. */}
        <p className="text-sm text-slate-500" data-id="element-485" data-mp="39b22x08bsz2u">
          People who have connected with your business.
        </p>

        {/* Three numbers, not a dashboard */}
        <div className="grid grid-cols-3 gap-3 lg:gap-4 mt-5" data-id="element-486" data-mp="3e7slpvbim8ry">
          <Summary label="All customers" value={all.length} data-id="element-487" data-mp="3iafepzjx9zqq" />
          <Summary label="New this month" value={newThisMonth} data-id="element-488" data-mp="3n1ykzx4mlxvn" />
          <Summary label="With orders" value={withOrders} data-id="element-489" data-mp="3n3i2ts5xkgzj" />
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mt-6" data-id="element-490" data-mp="2qjj3t4hfa563">
          <div className="relative flex-1" data-id="element-491" data-mp="2ulnx223ykzs7">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-492" data-mp="2ukcrbwqydhyg" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customers..." aria-label="Search customers" className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm text-dark-navy placeholder:text-slate-400 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-493" data-mp="2n1v5cr1w4aol" />
          </div>
          <div className="flex gap-3" data-id="element-494" data-mp="1zbhjji9wf82g">
            <PremiumSelect value={source} onChange={setSource} options={['All sources', 'My Shop', 'Website Chat', 'Talab Discover', 'Added manually']} data-id="element-495" data-mp="1udg0uygdxari" />
            <PremiumSelect value={activity} onChange={setActivity} options={['Any activity', 'Active this week', 'Has orders', 'No orders yet']} data-id="element-496" data-mp="1gdepgp2hukxs" />
          </div>
        </div>

        {list.length === 0 ? <div className="mt-6 py-16 text-center bg-white border border-slate-200 rounded-3xl" data-id="element-497" data-mp="36pv0cb6h40td">
            <span className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100 inline-flex items-center justify-center" data-id="element-498" data-mp="3bnwimtw2rw3s">
              <UsersIcon className="w-5 h-5 text-slate-400" data-id="element-499" data-mp="2xs2jh2l3sl3v" />
            </span>
            <p className="text-sm font-bold text-dark-navy mt-4" data-id="element-500" data-mp="31smjcdx2a2tp">
              {all.length === 0 ? 'No customers yet' : 'No customers match that'}
            </p>
            <p className="text-[13px] text-slate-500 mt-1.5 max-w-sm mx-auto leading-relaxed" data-id="element-501" data-mp="2sqvdn92itvzu">
              {all.length === 0 ? 'When someone signs in on your shop or chats with your assistant, they appear here.' : 'Try a different name, number or filter.'}
            </p>
          </div> : <>
            {/* Desktop table */}
            <div className="hidden lg:block mt-6 bg-white border border-slate-200 rounded-3xl overflow-hidden" data-id="element-502" data-mp="26jiz28nsqpn1">
              <table className="w-full" data-id="element-503" data-mp="2l8f0hmvcl4eo">
                <thead data-id="element-504" data-mp="2ggniw1u6690o">
                  <tr className="border-b border-slate-100" data-id="element-505" data-mp="2gfchu9s3zfd9">
                    {['Customer', 'Contact', 'Source', 'Orders', 'Last activity'].map(h => <th key={h} className={`px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 ${h === 'Orders' ? 'text-center' : 'text-left'}`} data-id="element-506" data-mp="2p97c2mrozle4">
                          {h}
                        </th>)}
                    <th className="px-6 py-4" data-id="element-507" data-mp="19s74636iepbg" />
                  </tr>
                </thead>
                <tbody data-id="element-508" data-mp="14uoweg48ag51">
                  {list.map(c => <tr key={c.id} onClick={() => setOpen(c)} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 cursor-pointer transition-colors duration-150 ease-out" data-id="element-509" data-mp="1uhrndccio662">
                      <td className="px-6 py-4" data-id="element-510" data-mp="28ghkut3907uy">
                        <div className="flex items-center gap-3" data-id="element-511" data-mp="28ic3u1uylpwe">
                          <span className="w-9 h-9 rounded-xl bg-primary-indigo/[0.07] flex items-center justify-center text-[11px] font-bold text-primary-indigo flex-shrink-0" data-id="element-512" data-mp="2dgncwadfjwdk">
                            {initials(c.name)}
                          </span>
                          <span className="text-sm font-bold text-dark-navy" data-id="element-513" data-mp="2maia8w8b8iin">
                            {c.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-514" data-mp="2w4iopophf041">
                        <p className="text-[13px] text-slate-600" data-id="element-515" data-mp="316hn0k6d76u4">{c.phone}</p>
                        {c.email && <p className="text-[12px] text-slate-400 truncate max-w-[180px]" data-id="element-516" data-mp="pjnvx0elira4">
                            {c.email}
                          </p>}
                      </td>
                      <td className="px-6 py-4" data-id="element-517" data-mp="flzr1hna03e8">
                        <span className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100 text-[11px] font-semibold text-slate-600" data-id="element-518" data-mp="alluowv1ehw1">
                          {c.source}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-519" data-mp="13a9jv3iwprev">
                        <span className={`text-sm font-bold ${c.orders > 0 ? 'text-dark-navy' : 'text-slate-300'}`} data-id="element-520" data-mp="yhzlcz3q7j0z">
                          {c.orders}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-[13px] text-slate-500" data-id="element-521" data-mp="1r0wrdnowuf0w">
                        {c.lastActivity}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-522" data-mp="ujpuxk0jd8uv">
                        <span className="text-[13px] font-semibold text-primary-indigo" data-id="element-523" data-mp="zbhcx65mm69e">
                          View
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden mt-6 space-y-3" data-id="element-524" data-mp="1e88enm2rvu26">
              {list.map(c => <motion.button key={c.id} onClick={() => setOpen(c)} className="w-full text-left p-5 bg-white border border-slate-200 rounded-2xl" data-id="element-525" data-mp="1ia3c2fhl1nao">
                  <div className="flex items-center gap-3" data-id="element-526" data-mp="198vr0z42uiyj">
                    <span className="w-10 h-10 rounded-xl bg-primary-indigo/[0.07] flex items-center justify-center text-xs font-bold text-primary-indigo flex-shrink-0" data-id="element-527" data-mp="19bi0536m96iv">
                      {initials(c.name)}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-528" data-mp="3mflmv7l8ef8e">
                      <p className="text-sm font-bold text-dark-navy truncate" data-id="element-529" data-mp="3mi7ut8c1awr5">
                        {c.name}
                      </p>
                      <p className="text-[13px] text-slate-500" data-id="element-530" data-mp="3vhbeoej0zg6p">{c.phone}</p>
                    </div>
                    <span className="text-[13px] font-semibold text-slate-400" data-id="element-531" data-mp="3qhqgi6bojbv1">
                      {c.orders} {c.orders === 1 ? 'order' : 'orders'}
                    </span>
                  </div>
                  <p className="text-[12px] text-slate-400 mt-3" data-id="element-532" data-mp="2yrloblhohkj9">
                    {c.source} · {c.lastActivity}
                  </p>
                </motion.button>)}
            </div>
          </>}
      </div>

      {open && <CustomerProfile customer={open} orders={ordersFor(open)} onClose={() => setOpen(null)} onOpenOrder={o => setOpenOrder(o)} data-id="element-533" data-mp="37pntmd8g2zcc" />}
      {openOrder && <OrderDetailDrawer order={openOrder} onClose={() => setOpenOrder(null)} onStatus={() => setOpenOrder(null)} onCancel={() => setOpenOrder(null)} data-id="element-534" data-mp="ecw3tkn0d9df" />}
    </div>;
}
function Summary({
  label,
  value
}: {
  label: string;
  value: number;
}) {
  return <div className="bg-white border border-slate-200 rounded-2xl px-5 py-4" data-id="element-535" data-mp="den7sjv92f5">
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-536" data-mp="fhba71gklh8">
        {label}
      </p>
      <p className="font-display font-bold text-2xl text-dark-navy mt-1.5" data-id="element-537" data-mp="3mspombodh70a">
        {value}
      </p>
    </div>;
}